import React, { useState, useEffect } from 'react';
import { Select, Space, Input, InputNumber } from 'antd';
import { useReactFlow, useStore } from '@xyflow/react';
import { INTERACTION_TYPES } from 'util/constants';
import styles from '../../event-timeline.module.css';

const { Option } = Select;

interface DestroyableParamsProps {
  nodeId: string;
  selectableAssets: any[];
}

interface DestroyableParams {
  interactionType: INTERACTION_TYPES;
  targetId: string;
  lifespan: number;
}

const DestroyableParams: React.FC<DestroyableParamsProps> = ({
  nodeId,
  selectableAssets,
}) => {
  const { setNodes } = useReactFlow();
  const node = useStore((state) => state.nodes.find((n) => n.id === nodeId));
  const destroyable = node?.data ?? ({} as any);
  const defaultParams: DestroyableParams = {
    interactionType: INTERACTION_TYPES.CLICK,
    targetId: '',
    lifespan: 3,
  };
  const [params, setParams] = useState<DestroyableParams>(
    destroyable.params || defaultParams
  );
  const [objectId, setObjectId] = useState<string>(
    destroyable.params?.targetId || ''
  );
  const [lifespan, setLifespan] = useState<number>(
    destroyable.params?.lifespan || 3
  );

  const updateNodeData = (params: DestroyableParams) => {
    setNodes((nodes) =>
      nodes.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                params,
              },
            }
          : n
      )
    );
  };

  function onChangeParams(params: DestroyableParams) {
    setParams(params);
    updateNodeData(params);
  }

  function onChangeObjectId(value: string) {
    setObjectId(value);
    onChangeParams({ ...params, targetId: value });
  }

  function onChangeLifespan(value: number | null) {
    const newLifespan = value || 1;
    setLifespan(newLifespan);
    onChangeParams({ ...params, lifespan: newLifespan });
  }

  useEffect(() => {
    setParams(destroyable.params || defaultParams);
    setObjectId(destroyable.params?.targetId || '');
    setLifespan(destroyable.params?.lifespan || 3);
  }, [node?.id]);

  return (
    <>
      <div className={styles.parameterContainer}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <span className={styles.label}>Target ID:</span>
          {selectableAssets?.length > 0 ? (
            <Select
              value={objectId}
              onChange={onChangeObjectId}
              placeholder='Select object'
              style={{ width: '100%' }}
            >
              {selectableAssets?.map((asset) => (
                <Option
                  key={
                    asset.props.asset.id || asset.props.asset.ref.current?.uuid
                  }
                  value={
                    asset.props.asset.id || asset.props.asset.ref.current?.uuid
                  }
                >
                  {asset.props.asset.name}
                </Option>
              ))}
            </Select>
          ) : (
            <Input
              value={params.targetId}
              onChange={(e) =>
                onChangeParams({ ...params, targetId: e.target.value })
              }
              placeholder='Enter Object ID'
            />
          )}
        </Space>
      </div>

      <div className={styles.parameterContainer}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <span className={styles.label}>
            Lifespan (Number of clicks to destroy):
          </span>
          <InputNumber
            min={1}
            value={lifespan}
            onChange={onChangeLifespan}
            placeholder='Enter lifespan'
            style={{ width: '100%' }}
          />
        </Space>
      </div>
    </>
  );
};

export default DestroyableParams;
